import { useMemo, useState } from 'react';
import { Trash } from 'phosphor-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Supplier } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplier } from '../../../../../../redux/services/suppliersService';
import { updateUserSupplierDetails } from '../../../../../../redux/globalSlices/authSlice';
import { openDillToast } from '../../../../../../utils/helpers';
import { DillAvatar, DillButton } from '../../../../../general/Dill';
import { DillDropdown } from '../../../../../general/Dill/DillDropdown';

export const AdminSupplierEmailsSection = ({ supplier }: { supplier?: Supplier | null }) => {
  const dispatch = useAppDispatch();
  const { supplierUsers } = useAppSelector((state) => state.supplier);
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  const adminEmails = useMemo(() => supplier?.adminSupplierEmails ?? [], [supplier]);
  const supplierRootEmail = supplier?.email ?? '';

  const adminUserOptions = useMemo(
    () =>
      supplierUsers
        .filter((u) => {
          const email = u.email?.toLowerCase() ?? '';
          return (
            email !== supplierRootEmail.toLowerCase() &&
            !adminEmails.map((e) => e.toLowerCase()).includes(email)
          );
        })
        .map((u) => ({
          label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email,
          value: u.id,
        })),
    [supplierUsers, adminEmails, supplierRootEmail]
  );

  const handleAdd = async () => {
    const userToAdd = supplierUsers.find((u) => u.id === selectedUserId);
    if (!userToAdd?.email) return;

    const next = [...adminEmails, userToAdd.email];
    setAdding(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: { adminSupplierEmails: next },
      })
    );
    setAdding(false);

    if (result.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Admin email added.' });
      setSelectedUserId('');
      dispatch(updateUserSupplierDetails({ adminSupplierEmails: next }));
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to add admin email.' });
    }
  };

  const handleRemove = async (emailToRemove: string) => {
    const next = adminEmails.filter((e) => e.toLowerCase() !== emailToRemove.toLowerCase());
    setRemovingEmail(emailToRemove);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: { adminSupplierEmails: next },
      })
    );
    setRemovingEmail(null);

    if (result.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Admin email removed.' });
      dispatch(updateUserSupplierDetails({ adminSupplierEmails: next }));
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to remove admin email.' });
    }
  };

  const getDisplayName = (email: string) => {
    const u = supplierUsers.find((usr) => usr.email?.toLowerCase() === email.toLowerCase());
    if (!u) return null;
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    return { name: name || email, initials: `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}` };
  };

  const allAdminEmails = useMemo(() => {
    const root = supplierRootEmail ? [supplierRootEmail] : [];
    const extra = adminEmails.filter((e) => e.toLowerCase() !== supplierRootEmail.toLowerCase());
    return [...root, ...extra];
  }, [adminEmails, supplierRootEmail]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Admin Access</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Users in this list can edit supplier settings.
        </p>
      </div>

      {/* Add row */}
      <div
        className="flex items-start gap-2"
        data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
        <div className="flex-1">
          <DillDropdown
            items={adminUserOptions}
            value={selectedUserId || null}
            onSelectChange={(opt) => setSelectedUserId(opt.value)}
            placeholder={
              adminUserOptions.length === 0 ? 'All users are already admins' : 'Select a user…'
            }
            disabled={!canEdit}
            isSearchable
            containerClassName="h-[36px]"
          />
        </div>
        <DillButton
          label="Add Admin"
          onClick={handleAdd}
          isLoading={adding}
          isDisabled={!canEdit || !selectedUserId || adding}
        />
      </div>

      {/* Admin list */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        {allAdminEmails.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <p className="text-xs text-gray-400">No admin emails configured.</p>
          </div>
        ) : (
          allAdminEmails.map((email, i) => {
            const display = getDisplayName(email);
            const isRoot = email.toLowerCase() === supplierRootEmail.toLowerCase();
            const isRemoving = removingEmail?.toLowerCase() === email.toLowerCase();
            return (
              <div
                key={email}
                className={`flex items-center justify-between px-4 py-2.5 bg-white hover:bg-gray-50 transition-colors ${
                  i < allAdminEmails.length - 1 ? 'border-b border-gray-100' : ''
                }`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <DillAvatar initials={display?.initials || email[0].toUpperCase()} size={28} />
                  <div className="min-w-0">
                    {display?.name && display.name !== email && (
                      <p className="text-xs font-semibold text-gray-800 m-0 truncate">
                        {display.name}
                      </p>
                    )}
                    <p className="text-[11px] text-gray-400 m-0 truncate">{email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  {isRoot && (
                    <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md">
                      Owner
                    </span>
                  )}
                  {!isRoot && (
                    <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                      <DillButton
                        variant="icon-red"
                        icon={<Trash size={14} />}
                        onClick={() => handleRemove(email)}
                        isLoading={isRemoving}
                        isDisabled={!canEdit || isRemoving}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
